/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  tituloBase,
  TablaFiltrable
} from "../../../shared";

const SEARCH_FIELDS = ["nombre", "letra"];

export default function ProcesoTipos() {
  const [loader, setLoader] = useState(true);
  const [procesoTipos, setProcesoTipos] = useState([]);

  // Sidebar crear
  const [sideBarCrear, setSideBarCrear] = useState(false);
  const [loaderCrear, setLoaderCrear] = useState(false);
  const [formCrearKey, setFormCrearKey] = useState(0);

  // Sidebar editar
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEditarKey, setFormEditarKey] = useState(0);

  // Menú contextual
  const [menuSeleccionado, setMenuSeleccionado] = useState(null);
  const menuRef = useRef(null);

  const toast = useRef(null);

  // Formulario de crear
  const crearFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true
          },
          {
            nombre: "letra",
            tipoitemformularioid: 1,
            etiqueta: "Código",
            requerido: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear tipo de proceso",
        callback: crearProcesoTipo,
        validar: true
      }
    ]
  };

  function crearProcesoTipo(camposFormulario) {
    setLoaderCrear(true);
    accessAPI(
      "POST",
      "admin/config/procesotipo",
      camposFormulario,
      (respuesta) => {
        setProcesoTipos((prev) => [...prev, respuesta]);
        setLoaderCrear(false);
        setSideBarCrear(false);
        toast.current?.show({
          severity: "success",
          summary: "Creado",
          detail: "Tipo de proceso creado correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderCrear(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear tipo de proceso",
          sticky: true
        });
      }
    );
  }

  // Formulario de editar
  function getEditarFormConfig() {
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "nombre",
              tipoitemformularioid: 1,
              etiqueta: "Nombre",
              requerido: true,
              respuesta: editando?.nombre
            },
            {
              nombre: "letra",
              tipoitemformularioid: 1,
              etiqueta: "Código",
              requerido: true,
              respuesta: editando?.letra
            }
          ]
        }
      ],
      botones: [
        {
          etiqueta: "Guardar",
          callback: guardarEdicion,
          validar: true
        }
      ]
    };
  }

  function guardarEdicion(camposFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/config/procesotipo/${editando.id}`,
      camposFormulario,
      () => {
        setProcesoTipos((prev) =>
          prev.map((p) =>
            p.id === editando.id
              ? {
                  ...p,
                  nombre: camposFormulario.nombre,
                  letra: camposFormulario.letra
                }
              : p
          )
        );
        setLoaderEditar(false);
        setSideBarEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Tipo de proceso actualizado correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar",
          sticky: true
        });
      }
    );
  }

  function eliminarProcesoTipo(id) {
    accessAPI(
      "DELETE",
      `admin/config/procesotipo/${id}`,
      null,
      () => {
        setProcesoTipos((prev) => prev.filter((p) => p.id !== id));
        toast.current?.show({
          severity: "success",
          summary: "Eliminado",
          detail: "Tipo de proceso eliminado correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar",
          sticky: true
        });
      }
    );
  }

  function abrirCrear() {
    setFormCrearKey((prev) => prev + 1);
    setSideBarCrear(true);
  }

  function abrirEditar(procesoTipo) {
    setEditando(procesoTipo);
    setFormEditarKey((prev) => prev + 1);
    setSideBarEditar(true);
  }

  const getMenuItems = () => [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => {
        if (menuSeleccionado) {
          abrirEditar(menuSeleccionado);
        }
      }
    },
    { separator: true },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        if (menuSeleccionado) {
          confirmDialog({
            message: `¿Está seguro que desea eliminar "${menuSeleccionado.nombre}"?`,
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Eliminar",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: () => eliminarProcesoTipo(menuSeleccionado.id)
          });
        }
      }
    }
  ];

  useEffect(() => {
    document.title = "Tipos de proceso";
    accessAPI(
      "GET",
      "admin/config/procesotipo/listado",
      null,
      (respuesta) => {
        setProcesoTipos(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar tipos de proceso",
          sticky: true
        });
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={getMenuItems()} popup ref={menuRef} />

      {/* Sidebar crear */}
      <Sidebar
        visible={sideBarCrear}
        onHide={() => setSideBarCrear(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Nuevo tipo de proceso</Titulo>
          <Formulario
            key={formCrearKey}
            config={crearFormConfig}
            loader={loaderCrear}
          />
        </div>
      </Sidebar>

      {/* Sidebar editar */}
      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Editar tipo de proceso</Titulo>
          <Formulario
            key={formEditarKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        </div>
      </Sidebar>

      {loader && (
        <div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1em",
              marginTop: "2em"
            }}
          >
            <Skeleton width="16rem" height="2rem" />
            <Skeleton width="2.5rem" height="2.5rem" />
          </div>
          <Skeleton width="100%" height="15rem" />
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            urlVolver="/config"
            botones={
              <Boton callback={abrirCrear}>Nuevo tipo de proceso</Boton>
            }
          >
            Tipos de proceso
          </Titulo>

          <Card>
            <TablaFiltrable
              value={procesoTipos}
              searchFields={SEARCH_FIELDS}
              anchoBusqueda="33%"
              nombreArchivo="tipos_proceso"
              size="small"
              emptyMessage="No hay tipos de proceso"
              rowHover
            >
              <Column field="nombre" header="Nombre" />
              <Column field="letra" header="Código" style={{ width: "6rem" }} />
              <Column
                body={(rowData) => (
                  <Boton
                    icono
                    negro
                    callback={(e) => {
                      setMenuSeleccionado(rowData);
                      menuRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v" />
                  </Boton>
                )}
                style={{ width: "3rem", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
